import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Enhanced Video Ads Hook
 * Supports both custom ads (from R2) and Google IMA SDK ads
 * Includes analytics tracking for impressions, views, skips, and clicks
 */

// Default Google VAST tag (skippable)
const DEFAULT_VAST_TAG = 'https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_preroll_skippable&sz=1920x1080|1280x720|640x480&ciu_szs=300x250%2C728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=';

// Generate a unique session ID for tracking
const generateSessionId = () => {
  return 'sess_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
};

export const useEnhancedVideoAds = ({
  videoRef,
  adContainerRef,
  enableAds = true,
  videoId = null,  // Optional: for tracking which video the ad was shown in
  onAdStart,
  onAdEnd,
  onAdError
}) => {
  const [adPlaying, setAdPlaying] = useState(false);
  const [adPaused, setAdPaused] = useState(false); // Track if ad is paused
  const [adType, setAdType] = useState(null); // 'custom' or 'google'
  const [customAd, setCustomAd] = useState(null);
  const [imaReady, setImaReady] = useState(false);
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [skipCountdown, setSkipCountdown] = useState(0);
  const [adSettings, setAdSettings] = useState(null);
  
  const adsLoaderRef = useRef(null);
  const adsManagerRef = useRef(null);
  const adDisplayContainerRef = useRef(null);
  const adBreaksPlayedRef = useRef(new Set()); // Track which ad breaks have played
  const initializedRef = useRef(false);
  const customAdVideoRef = useRef(null);
  const skipTimerRef = useRef(null);
  const adCheckIntervalRef = useRef(null);
  const sessionIdRef = useRef(generateSessionId());
  const quartileTrackedRef = useRef({ 25: false, 50: false, 75: false });
  const currentAdBreakRef = useRef(null);
  const adPlayingRef = useRef(false); // Ref version of adPlaying to avoid stale closures

  // Keep adPlayingRef in sync with adPlaying state
  useEffect(() => {
    adPlayingRef.current = adPlaying;
  }, [adPlaying]);

  // Helper to get ad slot dimensions - uses ad container if available, falls back to video element
  const getAdSlotDimensions = useCallback(() => {
    const adContainer = adContainerRef?.current;
    const videoElement = videoRef?.current;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // On mobile, try to get dimensions from the parent container if ad container has no dimensions
    // This handles the case where the ad container has opacity: 0 and may not report correct sizes
    let width = 0;
    let height = 0;
    
    // Try ad container first
    if (adContainer) {
      width = adContainer.clientWidth || adContainer.offsetWidth || 0;
      height = adContainer.clientHeight || adContainer.offsetHeight || 0;
      
      // If ad container has no dimensions, try its parent
      if ((width === 0 || height === 0) && adContainer.parentElement) {
        width = adContainer.parentElement.clientWidth || adContainer.parentElement.offsetWidth || 0;
        height = adContainer.parentElement.clientHeight || adContainer.parentElement.offsetHeight || 0;
      }
    }
    
    // Fall back to video element
    if ((width === 0 || height === 0) && videoElement) {
      width = videoElement.clientWidth || videoElement.offsetWidth || 0;
      height = videoElement.clientHeight || videoElement.offsetHeight || 0;
    }
    
    // On mobile in portrait, use reasonable defaults if dimensions are still 0
    if (width === 0 || height === 0) {
      if (isMobile) {
        // Use viewport width and calculate 16:9 aspect ratio height
        width = window.innerWidth;
        height = Math.floor(width * 9 / 16);
        console.log(`[Ads] Mobile fallback dimensions: ${width}x${height}`);
      } else {
        width = 1280;
        height = 720;
      }
    }
    
    console.log(`[Ads] getAdSlotDimensions: ${width}x${height}, mobile=${isMobile}`);
    return { width, height };
  }, [adContainerRef, videoRef]);

  // Track ad analytics event — uses ad_url as fallback if adId is null
  const trackAdEvent = useCallback(async (adId, eventType, extraData = {}) => {
    const adUrl = extraData.ad_url;
    if (!adId && !adUrl) return;
    
    try {
      const payload = {
        event_type: eventType,
        session_id: sessionIdRef.current,
        video_id: videoId,
        ad_break: currentAdBreakRef.current,
        ...extraData
      };
      if (adId) payload.ad_id = adId;
      if (adUrl) payload.ad_url = adUrl;
      await axios.post(`${API}/api/vod/admin/ads/track`, payload);
      console.log(`[AdAnalytics] Tracked ${eventType} for ad ${adId || adUrl}`);
    } catch (err) {
      console.error('[AdAnalytics] Failed to track event:', err);
    }
  }, [videoId]);

  // Fetch ad settings on mount
  useEffect(() => {
    if (!enableAds) return;
    
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/api/vod/admin/ads/settings`);
        setAdSettings(res.data);
        console.log('[Ads] Loaded ad settings:', res.data);
        // Log ad break times in a more readable format
        console.log('[Ads] Ad break schedule:',
          `Break2: ${res.data.ad_break_2_enabled ? `${res.data.ad_break_2_time}s (${(res.data.ad_break_2_time/60).toFixed(1)}min)` : 'disabled'}`,
          `Break3: ${res.data.ad_break_3_enabled ? `${res.data.ad_break_3_time}s (${(res.data.ad_break_3_time/60).toFixed(1)}min)` : 'disabled'}`,
          `Break4: ${res.data.ad_break_4_enabled ? `${res.data.ad_break_4_time}s (${(res.data.ad_break_4_time/60).toFixed(1)}min)` : 'disabled'}`,
          `Break5: ${res.data.ad_break_5_enabled ? `${res.data.ad_break_5_time}s (${(res.data.ad_break_5_time/60).toFixed(1)}min)` : 'disabled'}`
        );
      } catch (err) {
        console.error('[Ads] Failed to load ad settings:', err);
      }
    };
    
    fetchSettings();
  }, [enableAds]);
  
  // State for video-specific cue points from Ad Cue Point Editor
  const [videoCuePoints, setVideoCuePoints] = useState(null);
  const [hasCustomAdSettings, setHasCustomAdSettings] = useState(false); // Track if video uses Ad Cue Points
  const [cuePointsLoaded, setCuePointsLoaded] = useState(false); // Track if cue points API call completed
  const cuePointsTriggeredRef = useRef(new Set());
  
  // Fetch video-specific cue points IMMEDIATELY when videoId is available
  // This must happen before user clicks play so we know whether to skip global ads
  useEffect(() => {
    if (!videoId) {
      // No video ID - nothing to fetch
      return;
    }
    
    // Reset state when video changes
    setCuePointsLoaded(false);
    setHasCustomAdSettings(false);
    
    const fetchVideoCuePoints = async () => {
      try {
        console.log('[Ads] Fetching video cue points for videoId:', videoId);
        const res = await axios.get(`${API}/api/vod-admin/ad-cues/video/${videoId}/cue-points-for-player`);
        setVideoCuePoints(res.data);
        cuePointsTriggeredRef.current = new Set(); // Reset triggered cue points
        
        // Use has_active_ads to determine if we should skip global ads
        // has_active_ads is true ONLY if at least one ad (pre-roll, post-roll, or mid-roll) is enabled
        // If all are disabled, fall back to global Ad Placements
        const hasActiveAds = res.data?.has_active_ads === true;
        setHasCustomAdSettings(hasActiveAds);
        
        console.log('[Ads] Loaded video cue points:', res.data);
        console.log('[Ads] has_custom_settings from API:', res.data?.has_custom_settings);
        console.log('[Ads] has_active_ads from API:', res.data?.has_active_ads);
        console.log('[Ads] Setting hasCustomAdSettings to:', hasActiveAds);
        
        if (hasActiveAds) {
          console.log('[Ads] ✅ Video has ACTIVE custom Ad Cue Points - GLOBAL ADS WILL BE SKIPPED');
        } else if (res.data?.has_custom_settings) {
          console.log('[Ads] ⚠️ Video has Ad Cue Points entry but ALL ADS DISABLED - falling back to global Ad Placements');
        } else {
          console.log('[Ads] ⚠️ Video has NO Ad Cue Points entry - using global Ad Placements');
        }
      } catch (err) {
        console.error('[Ads] Failed to load video cue points:', err);
        setHasCustomAdSettings(false);
      } finally {
        setCuePointsLoaded(true);
        console.log('[Ads] Cue points loading complete, cuePointsLoaded=true');
      }
    };
    
    fetchVideoCuePoints();
  }, [videoId]); // Only depend on videoId - fetch immediately when video loads

  // Load IMA SDK script
  useEffect(() => {
    if (!enableAds) return;
    
    if (window.google?.ima) {
      setImaReady(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
    script.async = true;
    
    script.onload = () => {
      console.log('[Ads] IMA SDK loaded');
      setImaReady(true);
    };
    
    script.onerror = (e) => {
      console.error('[Ads] Failed to load IMA SDK:', e);
    };
    
    document.head.appendChild(script);
  }, [enableAds]);

  // Initialize Google IMA
  const initializeIMA = useCallback((vastTag) => {
    const videoElement = videoRef?.current;
    const adContainer = adContainerRef?.current;

    if (!videoElement || !adContainer || !window.google?.ima) {
      return false;
    }

    try {
      // Create ad display container
      adDisplayContainerRef.current = new window.google.ima.AdDisplayContainer(
        adContainer,
        videoElement
      );

      // Create ads loader
      adsLoaderRef.current = new window.google.ima.AdsLoader(adDisplayContainerRef.current);

      // Handle ads manager loaded
      adsLoaderRef.current.addEventListener(
        window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        (event) => onAdsManagerLoaded(event, vastTag),
        false
      );

      // Handle ad errors
      adsLoaderRef.current.addEventListener(
        window.google.ima.AdErrorEvent.Type.AD_ERROR,
        (event) => {
          console.error('[Ads] Loader error:', event.getError());
          setAdPlaying(false);
          setAdType(null);
          // CRITICAL: Restore video visibility on error
          if (videoElement) {
            videoElement.style.opacity = '1';
            videoElement.style.visibility = 'visible';
            videoElement._isRestoringContent = false;
          }
          onAdError?.(event.getError()?.toString());
          videoElement?.play().catch(() => {});
        },
        false
      );

      initializedRef.current = true;
      return true;
    } catch (error) {
      console.error('[Ads] Error initializing IMA:', error);
      return false;
    }
  }, [videoRef, adContainerRef, onAdError]);

  // Handle ads manager loaded event
  const onAdsManagerLoaded = useCallback(async (adsManagerLoadedEvent, vastTag) => {
    const videoElement = videoRef?.current;
    const adContainer = adContainerRef?.current;
    
    try {
      const adsRenderingSettings = new window.google.ima.AdsRenderingSettings();
      adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;
      adsRenderingSettings.enablePreloading = true;
      adsRenderingSettings.bitrate = 2500; // Request higher quality ads (2500 kbps = HD)
      adsRenderingSettings.loadVideoTimeout = 15000; // Allow more time for HD ads to load

      adsManagerRef.current = adsManagerLoadedEvent.getAdsManager(
        videoElement,
        adsRenderingSettings
      );

      const am = adsManagerRef.current;

      // Content pause - ad starting
      am.addEventListener(window.google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, () => {
        console.log('[Ads] Google ad starting - CONTENT_PAUSE_REQUESTED');
        setAdPlaying(true);
        setAdType('google');
        videoElement?.pause();
        onAdStart?.();
      });
      
      // Ad started - log details for debugging
      am.addEventListener(window.google.ima.AdEvent.Type.STARTED, (adEvent) => {
        console.log('[Ads] Google ad STARTED');
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        // CRITICAL: Mark pre-roll as played immediately so it doesn't replay after skip
        adBreaksPlayedRef.current.add(1);
        console.log('[Ads] Marked pre-roll (break 1) as played');
        
        // BULLETPROOF: Set flag on video element that pre-roll was played
        // This flag persists even if React state is reset
        if (videoElement) {
          videoElement._preRollPlayed = true;
          console.log('[Ads] Set _preRollPlayed flag on video element');
        }
        
        try {
          const ad = adEvent.getAd();
          const mediaUrl = ad?.getMediaUrl?.();
          const adDuration = ad?.getDuration?.() || 30;
          const skipOffset = ad?.getSkipTimeOffset?.() || 5;
          
          console.log('[Ads] Ad details:', {
            adId: ad?.getAdId?.(),
            mediaUrl: mediaUrl,
            duration: adDuration,
            skipOffset: skipOffset,
            isLinear: ad?.isLinear?.(),
            vastMediaWidth: ad?.getVastMediaWidth?.(),
            vastMediaHeight: ad?.getVastMediaHeight?.()
          });
          
          // On mobile, if we have a media URL, play it directly in our video element
          // This is custom playback mode - bypassing the IMA SDK's broken iframes
          if (isMobile && mediaUrl && videoElement) {
            console.log('[Ads] MOBILE CUSTOM PLAYBACK - Playing ad directly in video element');
            
            // Mark that we're doing mobile custom playback
            videoElement._mobileCustomPlayback = true;
            videoElement._adDuration = adDuration;
            videoElement._skipOffset = skipOffset;
            // Flag to skip pre-roll check when restoring content
            videoElement._skipPreRollOnRestore = true;
            
            // CRITICAL: Hide the ad container overlay so the video shows through!
            if (adContainer) {
              adContainer.style.display = 'none';
              adContainer.style.visibility = 'hidden';
              console.log('[Ads] Hidden ad container overlay to show video');
            }
            
            // Save original video state (only if not already saved)
            // CRITICAL: Use the position saved BEFORE the ad was requested (from VODVideoDetail)
            // This prevents the "originalTime = 0" bug
            if (!videoElement._adPlaybackState) {
              const savedPosition = videoElement._savedPositionBeforeAd || videoElement.currentTime || 0;
              videoElement._adPlaybackState = {
                originalSrc: videoElement.src,
                originalTime: savedPosition,
                originalPoster: videoElement.poster
              };
              console.log('[Ads] Saved original video state, position:', savedPosition);
              // Clear the saved position now that we've used it
              delete videoElement._savedPositionBeforeAd;
            }
            
            // Remove any existing ended listener before adding new one
            if (videoElement._adEndedListener) {
              videoElement.removeEventListener('ended', videoElement._adEndedListener);
              videoElement._adEndedListener = null;
            }
            
            // CRITICAL: Ensure video is hidden BEFORE changing source to prevent flash
            videoElement.style.opacity = '0';
            videoElement.style.visibility = 'hidden';
            
            // Switch to ad video source
            videoElement.src = mediaUrl;
            videoElement.poster = '';
            videoElement.load();
            
            // Wait for ad video to be ready, then show and play
            const onAdCanPlay = () => {
              videoElement.removeEventListener('canplay', onAdCanPlay);
              videoElement.removeEventListener('loadeddata', onAdCanPlay);
              // Now show video with ad content
              videoElement.style.opacity = '1';
              videoElement.style.visibility = 'visible';
              videoElement.play().then(() => {
                console.log('[Ads] Mobile ad playing successfully');
              }).catch(e => {
                console.error('[Ads] Failed to play ad:', e);
                finishMobileAd();
              });
            };
            videoElement.addEventListener('canplay', onAdCanPlay);
            videoElement.addEventListener('loadeddata', onAdCanPlay); // Fallback for iOS
            
            // Listen for ad video to end - store the listener so we can remove it on skip
            const onAdVideoEnded = () => {
              console.log('[Ads] Mobile ad video ENDED - finishing ad');
              videoElement.removeEventListener('ended', onAdVideoEnded);
              videoElement._adEndedListener = null;
              finishMobileAd();
            };
            videoElement._adEndedListener = onAdVideoEnded;
            videoElement.addEventListener('ended', onAdVideoEnded);
          }
        } catch (e) {
          console.log('[Ads] Could not handle ad start:', e);
        }
        
        // Log ad container state
        if (adContainer) {
          console.log('[Ads] Ad container state:', {
            width: adContainer.clientWidth,
            height: adContainer.clientHeight,
            childCount: adContainer.children.length,
            hasIframe: adContainer.querySelector('iframe') !== null,
            hasVideo: adContainer.querySelector('video') !== null
          });
        }
      });
      
      // Helper function to finish mobile ad and restore content
      const finishMobileAd = () => {
        console.log('[Ads] finishMobileAd called');
        
        // CRITICAL: Check if already restoring to prevent double-calls
        if (videoElement._isRestoringContent) {
          console.log('[Ads] Already restoring content - ignoring duplicate call');
          return;
        }
        
        if (!videoElement?._adPlaybackState) {
          console.log('[Ads] No saved state to restore - just making video visible');
          // Even without saved state, make sure video is visible and playable
          if (videoElement) {
            videoElement.style.opacity = '1';
            videoElement.style.visibility = 'visible';
          }
          setAdPlaying(false);
          setAdType(null);
          onAdEnd?.();
          return;
        }
        
        // CRITICAL: Set flag IMMEDIATELY to prevent any race conditions
        videoElement._isRestoringContent = true;
        videoElement._preRollPlayed = true; // Ensure this stays set
        
        // CRITICAL: Remove the ended event listener to prevent double-calling
        if (videoElement._adEndedListener) {
          videoElement.removeEventListener('ended', videoElement._adEndedListener);
          videoElement._adEndedListener = null;
          console.log('[Ads] Removed ad ended listener');
        }
        
        // Clear mobile playback flag
        videoElement._mobileCustomPlayback = false;
        
        // Restore original video
        const state = videoElement._adPlaybackState;
        console.log('[Ads] Restoring content video, position:', state.originalTime);
        
        // Keep video hidden during source switch
        videoElement.style.opacity = '0';
        videoElement.style.visibility = 'hidden';
        
        videoElement.src = state.originalSrc;
        videoElement.poster = state.originalPoster || '';
        videoElement._adPlaybackState = null;
        
        videoElement.load();
        
        // Flag to prevent double-handling
        let contentRestored = false;
        
        // Function to restore and play content
        const restoreContent = () => {
          if (contentRestored) return;
          contentRestored = true;
          
          videoElement.removeEventListener('canplay', restoreContent);
          videoElement.removeEventListener('loadeddata', restoreContent);
          
          videoElement.currentTime = state.originalTime;
          
          // CRITICAL: Make video visible BEFORE attempting play
          videoElement.style.opacity = '1';
          videoElement.style.visibility = 'visible';
          console.log('[Ads] Video visible - content ready');
          
          videoElement.play().then(() => {
            console.log('[Ads] Content video resumed at:', state.originalTime);
            setTimeout(() => {
              videoElement._isRestoringContent = false;
              console.log('[Ads] Content restoration complete, flag cleared');
            }, 1500);
          }).catch(e => {
            console.error('[Ads] Failed to auto-resume content:', e);
            // On mobile, autoplay might be blocked - that's OK, user can tap play
            // But make sure video is visible so they CAN tap play
            videoElement.style.opacity = '1';
            videoElement.style.visibility = 'visible';
            videoElement._isRestoringContent = false;
          });
        };
        
        // Listen for video ready events
        videoElement.addEventListener('canplay', restoreContent);
        videoElement.addEventListener('loadeddata', restoreContent);
        
        // SAFETY: If events don't fire within 3 seconds, force restore
        setTimeout(() => {
          if (!contentRestored) {
            console.log('[Ads] Timeout waiting for video ready - forcing restore');
            restoreContent();
          }
        }, 3000);
        
        // CRITICAL FIX: Do NOT show ad container after mobile custom playback!
        console.log('[Ads] Keeping ad container hidden after mobile custom playback');
        
        // Update state
        setAdPlaying(false);
        setAdType(null);
        onAdEnd?.();
      };
      
      // Expose finishMobileAd so it can be called from skip button
      videoElement._finishMobileAd = finishMobileAd;

      // Content resume - ad ended (from IMA SDK - may not fire on mobile custom playback)
      am.addEventListener(window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () => {
        console.log('[Ads] Google ad ended - CONTENT_RESUME_REQUESTED');
        
        // If we already handled this via mobile custom playback, skip
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile && !videoElement?._adPlaybackState) {
          console.log('[Ads] Mobile custom playback already handled this');
          return;
        }
        
        setAdPlaying(false);
        setAdType(null);
        
        // On mobile, restore the original content video
        if (isMobile && videoElement?._adPlaybackState) {
          finishMobileAd();
        } else {
          videoElement?.play().catch(() => {});
          onAdEnd?.();
        }
      });

      // All ads completed
      am.addEventListener(window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED, () => {
        setAdPlaying(false);
        setAdType(null);
        onAdEnd?.();
      });

      // Ad skipped
      am.addEventListener(window.google.ima.AdEvent.Type.SKIPPED, () => {
        setAdPlaying(false);
        setAdType(null);
      });
      
      // Ad loaded - media is ready to play
      am.addEventListener(window.google.ima.AdEvent.Type.LOADED, (adEvent) => {
        console.log('[Ads] Ad LOADED - media ready');
        try {
          const ad = adEvent.getAd();
          console.log('[Ads] Loaded ad info:', {
            isLinear: ad?.isLinear?.(),
            width: ad?.getWidth?.(),
            height: ad?.getHeight?.(),
            vastMediaWidth: ad?.getVastMediaWidth?.(),
            vastMediaHeight: ad?.getVastMediaHeight?.(),
            mediaUrl: ad?.getMediaUrl?.()
          });
          
          // On iOS, fix any video elements inside the ad container
          const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
          if (isIOS && adContainer) {
            // Small delay to let IMA create its elements
            setTimeout(() => {
              const imaVideos = adContainer.querySelectorAll('video');
              const imaIframes = adContainer.querySelectorAll('iframe');
              
              console.log('[Ads] iOS - Found elements:', { videos: imaVideos.length, iframes: imaIframes.length });
              
              // Force all videos to be visible and properly sized
              imaVideos.forEach(v => {
                v.style.cssText = 'width:100%!important;height:100%!important;position:absolute!important;top:0!important;left:0!important;object-fit:contain!important;opacity:1!important;visibility:visible!important;z-index:1!important;';
                v.setAttribute('playsinline', '');
                v.setAttribute('webkit-playsinline', '');
              });
              
              // Force all iframes to be visible
              imaIframes.forEach(f => {
                f.style.cssText = 'width:100%!important;height:100%!important;position:absolute!important;top:0!important;left:0!important;opacity:1!important;visibility:visible!important;z-index:1!important;';
              });
            }, 100);
          }
        } catch (e) {
          console.log('[Ads] Could not get loaded ad info:', e);
        }
      });

      // Ad error
      am.addEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, (event) => {
        console.error('[Ads] Manager error:', event.getError());
        setAdPlaying(false);
        setAdType(null);
        // CRITICAL: Restore video visibility on error
        if (videoElement) {
          videoElement.style.opacity = '1';
          videoElement.style.visibility = 'visible';
          videoElement._isRestoringContent = false;
        }
        videoElement?.play().catch(() => {});
        onAdError?.(event.getError()?.toString());
      });

      // Use ad container dimensions for IMA initialization
      // CRITICAL: Ensure the ad container is visible and has dimensions BEFORE init
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // Force ad container to be visible for dimension calculation
      if (adContainer) {
        adContainer.style.visibility = 'visible';
        adContainer.style.opacity = '1';
        adContainer.style.display = 'block';
        adContainer.style.position = 'absolute';
        adContainer.style.top = '0';
        adContainer.style.left = '0';
        adContainer.style.width = '100%';
        adContainer.style.height = '100%';
        adContainer.style.zIndex = '10000';
      }
      
      // Wait a frame for dimensions to be calculated on mobile
      if (isMobile) {
        await new Promise(resolve => requestAnimationFrame(resolve));
      }
      
      const { width, height } = getAdSlotDimensions();
      
      // Ensure valid dimensions - use video element as fallback
      let finalWidth = width;
      let finalHeight = height;
      
      if (finalWidth < 100 || finalHeight < 50) {
        // Fallback to video element dimensions
        finalWidth = videoElement?.clientWidth || window.innerWidth;
        finalHeight = videoElement?.clientHeight || Math.round(window.innerWidth * 9 / 16);
        console.log(`[Ads] Using fallback dimensions: ${finalWidth}x${finalHeight}`);
      }
      
      // Ensure minimum HD dimensions for desktop to get HD quality ads
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (!isMobileDevice && finalWidth < 1280) {
        finalWidth = Math.max(finalWidth, 1280);
        finalHeight = Math.max(finalHeight, 720);
        console.log(`[Ads] Bumped to HD minimum: ${finalWidth}x${finalHeight}`);
      }
      
      console.log(`[Ads] Initializing IMA with size: ${finalWidth}x${finalHeight}`);
      am.init(finalWidth, finalHeight, window.google.ima.ViewMode.NORMAL);
      am.start();
    } catch (error) {
      console.error('[Ads] Error setting up ads manager:', error);
      setAdPlaying(false);
      setAdType(null);
      videoElement?.play().catch(() => {});
    }
  }, [videoRef, adContainerRef, onAdStart, onAdEnd, onAdError]);

  // Resize IMA ads to match current container/viewport size
  // Call this when fullscreen changes or orientation changes
  const resizeImaAds = useCallback(() => {
    if (!adsManagerRef.current) {
      console.log('[Ads] Cannot resize - no ads manager');
      return;
    }
    
    // Check if we're in CSS fullscreen mode (landscape mobile)
    const isFullscreenMode = document.querySelector('.vod-landscape-fullscreen') !== null;
    const isLandscape = window.innerWidth > window.innerHeight;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    let width, height;
    
    if ((isMobile && isLandscape) || isFullscreenMode) {
      // Use full viewport for mobile fullscreen
      width = window.innerWidth;
      height = window.innerHeight;
      console.log(`[Ads] Fullscreen mode - using viewport: ${width}x${height}`);
    } else {
      // Use ad container dimensions
      const dims = getAdSlotDimensions();
      width = dims.width;
      height = dims.height;
      console.log(`[Ads] Normal mode - using container: ${width}x${height}`);
    }
    
    console.log(`[Ads] Resizing IMA to ${width}x${height}`);
    
    try {
      adsManagerRef.current.resize(width, height, window.google.ima.ViewMode.NORMAL);
    } catch (e) {
      console.error('[Ads] Error resizing IMA:', e);
    }
  }, [getAdSlotDimensions]);

  // Play custom ad from R2
  const playCustomAd = useCallback((ad, adBreakNum = 1) => {
    const adContainer = adContainerRef?.current;
    if (!adContainer || !ad) {
      console.error('[Ads] Cannot play custom ad - container or ad missing', { hasContainer: !!adContainer, hasAd: !!ad });
      return;
    }

    console.log('[Ads] Playing custom ad:', ad.title, 'Break:', adBreakNum);
    currentAdBreakRef.current = adBreakNum;
    setAdPlaying(true);
    setAdType('custom');
    setCustomAd(ad);
    setShowSkipButton(false);
    setSkipCountdown(ad.skip_after || 5);
    quartileTrackedRef.current = { 25: false, 50: false, 75: false };
    onAdStart?.();
    
    // Track impression
    trackAdEvent(ad.id, 'impression', { ad_url: ad.video_url });

    // Create custom ad video element — do NOT set src or autoplay yet
    // to prevent race condition where play event fires before listeners are attached
    const adVideo = document.createElement('video');
    // z-index 10002 - above container (10000) but below controls (10010)
    adVideo.style.cssText = 'width:100%;height:100%;object-fit:contain;position:absolute;top:0;left:0;z-index:10002;background:black;';
    adVideo.playsInline = true;
    adVideo.setAttribute('playsinline', ''); // iOS requirement
    adVideo.setAttribute('webkit-playsinline', ''); // Older iOS
    adVideo.muted = false; // Ensure audio plays
    adVideo.preload = 'auto';

    // CRITICAL: Attach ALL event listeners BEFORE setting src or appending to DOM
    // This prevents the race condition where autoplay fires before listeners exist

    // Track start event when video actually starts playing
    adVideo.addEventListener('play', () => {
      trackAdEvent(ad.id, 'start', { ad_url: ad.video_url });
    }, { once: true });

    // Track quartile progress
    adVideo.addEventListener('timeupdate', () => {
      const duration = adVideo.duration;
      const currentTime = adVideo.currentTime;
      if (!duration || duration === 0) return;
      
      const percentComplete = (currentTime / duration) * 100;
      
      if (percentComplete >= 25 && !quartileTrackedRef.current[25]) {
        quartileTrackedRef.current[25] = true;
        trackAdEvent(ad.id, 'quartile_25', { watch_duration: currentTime, ad_url: ad.video_url });
      }
      if (percentComplete >= 50 && !quartileTrackedRef.current[50]) {
        quartileTrackedRef.current[50] = true;
        trackAdEvent(ad.id, 'quartile_50', { watch_duration: currentTime, ad_url: ad.video_url });
      }
      if (percentComplete >= 75 && !quartileTrackedRef.current[75]) {
        quartileTrackedRef.current[75] = true;
        trackAdEvent(ad.id, 'quartile_75', { watch_duration: currentTime, ad_url: ad.video_url });
      }
    });

    // Handle click on ad — append UTM params for conversion tracking
    adVideo.addEventListener('click', () => {
      if (ad.click_url) {
        // Track click
        trackAdEvent(ad.id, 'click', { watch_duration: adVideo.currentTime, ad_url: ad.video_url });
        if (ad.id) {
          axios.post(`${API}/api/vod/admin/ads/track-click/${ad.id}`).catch(() => {});
        }
        let ctaUrl = ad.click_url;
        try {
          const u = new URL(ctaUrl);
          u.searchParams.set('utm_source', 'ltv_ad');
          u.searchParams.set('utm_medium', 'video_ad');
          u.searchParams.set('utm_campaign', (ad.id || 'custom_ad').substring(0, 24));
          ctaUrl = u.toString();
        } catch {}
        window.open(ctaUrl, '_blank');
      }
    });

    // Handle ad end
    adVideo.addEventListener('ended', () => {
      console.log('[Ads] Custom ad video ended naturally');
      trackAdEvent(ad.id, 'complete', { watch_duration: adVideo.duration, ad_url: ad.video_url });
      endCustomAd();
    });
    
    // Handle ad error
    adVideo.addEventListener('error', (e) => {
      const errorMsg = adVideo.error?.message || 'Video load failed';
      console.error('[Ads] Custom ad video error:', e, adVideo.error);
      endCustomAd(`Ad video error: ${errorMsg}`);
    });

    // NOW set src after all listeners are attached
    adVideo.src = ad.video_url;
    
    customAdVideoRef.current = adVideo;
    adContainer.innerHTML = '';
    adContainer.appendChild(adVideo);
    
    // Make container visible using multiple methods for compatibility
    adContainer.style.display = 'block';
    adContainer.style.visibility = 'visible';
    adContainer.style.opacity = '1';
    adContainer.style.zIndex = '10000';
    adContainer.style.backgroundColor = 'black';
    
    console.log('[Ads] Ad container made visible, attempting to play video...');

    // Handle skip countdown
    let countdown = ad.skip_after || 5;
    if (countdown > 0) {
      skipTimerRef.current = setInterval(() => {
        countdown--;
        setSkipCountdown(countdown);
        if (countdown <= 0) {
          clearInterval(skipTimerRef.current);
          setShowSkipButton(true);
        }
      }, 1000);
    } else {
      // Not skippable
      setShowSkipButton(false);
    }

    // Try to play with retry logic for mobile (no autoplay attribute — explicit play only)
    const attemptPlay = async () => {
      try {
        await adVideo.play();
        console.log('[Ads] Custom ad video started playing successfully');
      } catch (err) {
        console.error('[Ads] Error playing custom ad:', err.name, err.message);
        
        // On mobile, autoplay might fail - try muted first then unmute
        if (err.name === 'NotAllowedError') {
          console.log('[Ads] Trying muted playback...');
          adVideo.muted = true;
          try {
            await adVideo.play();
            console.log('[Ads] Muted playback started, will unmute after user interaction');
            // Try to unmute after a short delay
            setTimeout(() => {
              adVideo.muted = false;
            }, 100);
          } catch (mutedErr) {
            console.error('[Ads] Even muted playback failed:', mutedErr);
            endCustomAd(`Playback blocked: ${mutedErr.message}`);
          }
        } else {
          endCustomAd(`Play failed: ${err.message}`);
        }
      }
    };
    
    attemptPlay();
  }, [adContainerRef, onAdStart, trackAdEvent]);

  // End custom ad
  const endCustomAd = useCallback((errorMessage = null) => {
    console.log('[Ads] Ending custom ad, cleaning up...', errorMessage ? `Error: ${errorMessage}` : '');
    
    if (skipTimerRef.current) {
      clearInterval(skipTimerRef.current);
    }
    
    if (customAdVideoRef.current) {
      customAdVideoRef.current.pause();
      customAdVideoRef.current.remove();
      customAdVideoRef.current = null;
    }
    
    if (adContainerRef?.current) {
      adContainerRef.current.innerHTML = '';
      // Reset all visibility properties
      adContainerRef.current.style.display = 'none';
      adContainerRef.current.style.visibility = 'hidden';
      adContainerRef.current.style.opacity = '0';
    }
    
    setAdPlaying(false);
    setAdType(null);
    setCustomAd(null);
    setShowSkipButton(false);
    
    if (errorMessage) {
      onAdError?.(errorMessage);
    } else {
      onAdEnd?.();
    }
    
    // Play the main video
    videoRef?.current?.play().catch(() => {});
  }, [adContainerRef, videoRef, onAdEnd]);

  // Skip custom ad
  const skipCustomAd = useCallback(() => {
    if (showSkipButton && customAd) {
      // Track skip event with timing
      const watchDuration = customAdVideoRef.current?.currentTime || 0;
      trackAdEvent(customAd.id, 'skip', { 
        watch_duration: watchDuration,
        skip_time: watchDuration,
        ad_url: customAd.video_url 
      });
      endCustomAd();
    }
  }, [showSkipButton, customAd, endCustomAd, trackAdEvent]);

  // Pause the currently playing ad (works for both IMA and custom ads)
  const pauseAd = useCallback(() => {
    console.log('[Ads] pauseAd called - adPlaying:', adPlaying, 'adType:', adType);
    
    if (!adPlaying) {
      console.log('[Ads] Cannot pause - no ad playing');
      return;
    }
    
    if (adType === 'google' && adsManagerRef.current) {
      // Pause IMA/VAST ad
      try {
        adsManagerRef.current.pause();
        setAdPaused(true);
        console.log('[Ads] IMA ad paused');
      } catch (e) {
        console.error('[Ads] Error pausing IMA ad:', e);
      }
    } else if (adType === 'custom') {
      // Pause custom ad - check the ref
      if (customAdVideoRef.current) {
        customAdVideoRef.current.pause();
        setAdPaused(true);
        console.log('[Ads] Custom ad paused via ref');
      } else {
        // Fallback: find the ad video in the ad container
        const adContainer = adContainerRef?.current;
        const adVideo = adContainer?.querySelector('video');
        if (adVideo) {
          adVideo.pause();
          setAdPaused(true);
          console.log('[Ads] Custom ad paused via container query');
        } else {
          console.log('[Ads] Could not find custom ad video to pause');
        }
      }
    } else {
      console.log('[Ads] Unknown ad type or missing manager:', adType);
    }
  }, [adPlaying, adType, adContainerRef]);

  // Resume the currently paused ad
  const resumeAd = useCallback(() => {
    console.log('[Ads] resumeAd called - adPlaying:', adPlaying, 'adPaused:', adPaused, 'adType:', adType);
    
    if (!adPlaying || !adPaused) {
      console.log('[Ads] Cannot resume - adPlaying:', adPlaying, 'adPaused:', adPaused);
      return;
    }
    
    if (adType === 'google' && adsManagerRef.current) {
      // Resume IMA/VAST ad
      try {
        adsManagerRef.current.resume();
        setAdPaused(false);
        console.log('[Ads] IMA ad resumed');
      } catch (e) {
        console.error('[Ads] Error resuming IMA ad:', e);
      }
    } else if (adType === 'custom') {
      // Resume custom ad - check the ref
      if (customAdVideoRef.current) {
        customAdVideoRef.current.play().catch(e => console.error('[Ads] Error playing custom ad:', e));
        setAdPaused(false);
        console.log('[Ads] Custom ad resumed via ref');
      } else {
        // Fallback: find the ad video in the ad container
        const adContainer = adContainerRef?.current;
        const adVideo = adContainer?.querySelector('video');
        if (adVideo) {
          adVideo.play().catch(e => console.error('[Ads] Error playing custom ad:', e));
          setAdPaused(false);
          console.log('[Ads] Custom ad resumed via container query');
        } else {
          console.log('[Ads] Could not find custom ad video to resume');
        }
      }
    } else {
      console.log('[Ads] Unknown ad type or missing manager:', adType);
    }
  }, [adPlaying, adPaused, adType, adContainerRef]);

  // Toggle ad play/pause
  const toggleAdPlayPause = useCallback(() => {
    console.log('[Ads] toggleAdPlayPause called - adPaused:', adPaused);
    if (adPaused) {
      resumeAd();
    } else {
      pauseAd();
    }
  }, [adPaused, pauseAd, resumeAd]);

  // Play pre-roll ad (Ad Break 1 or video-specific pre-roll)
  const playPreRoll = useCallback(async () => {
    const videoElement = videoRef?.current;
    
    // BULLETPROOF CHECK: If pre-roll was already played on this video element, NEVER play again
    if (videoElement?._preRollPlayed) {
      console.log('[Ads] BLOCKED - _preRollPlayed flag is set, ad already played');
      return false;
    }
    
    // CRITICAL: Check if content is being restored after an ad - don't re-trigger pre-roll
    if (videoElement?._isRestoringContent) {
      console.log('[Ads] Content is being restored - skipping pre-roll check');
      return false;
    }
    
    // Check if already played
    if (!enableAds || adBreaksPlayedRef.current.has(1)) {
      console.log('[Ads] Pre-roll already in adBreaksPlayedRef - skipping');
      return false;
    }
    
    // IMPORTANT: Wait for cue points to load before deciding which ads to play
    // This prevents the global Ad Placements from playing before we know if video has custom settings
    if (!cuePointsLoaded) {
      console.log('[Ads] Waiting for cue points to load before playing pre-roll...');
      return false;
    }
    
    // First check video-specific pre-roll from cue point editor
    if (videoCuePoints?.pre_roll) {
      adBreaksPlayedRef.current.add(1);
      console.log('[Ads] Playing video-specific pre-roll from Ad Cue Points...');
      
      const preRoll = videoCuePoints.pre_roll;
      if (preRoll.ad_type === 'custom' && preRoll.custom_url) {
        const customAdData = {
          id: 'pre_roll_cue',
          video_url: preRoll.custom_url,
          duration: 15,
          skip_after: 5
        };
        playCustomAd(customAdData, 'pre_roll');
        return true;
      } else {
        // Google ad for pre-roll
        const tagToUse = adSettings?.vast_tag_url || DEFAULT_VAST_TAG;
        if (!initializedRef.current) {
          const initialized = initializeIMA(tagToUse);
          if (!initialized) return false;
        }
        
        if (!adsLoaderRef.current || !videoElement) return false;
        
        adDisplayContainerRef.current?.initialize();
        const correlator = Date.now();
        const tagWithCorrelator = tagToUse + `&correlator=${correlator}`;
        
        const preRollDims = getAdSlotDimensions();
        const adsRequest = new window.google.ima.AdsRequest();
        adsRequest.adTagUrl = tagWithCorrelator;
        adsRequest.linearAdSlotWidth = preRollDims.width;
        adsRequest.linearAdSlotHeight = preRollDims.height;
        
        adsLoaderRef.current.requestAds(adsRequest);
        return true;
      }
    }
    
    // If video has custom Ad Cue Points settings, skip global Ad Placements entirely
    // This is the key check - if hasCustomAdSettings is true, don't play global ads
    if (hasCustomAdSettings) {
      console.log('[Ads] Video has custom Ad Cue Points configured - SKIPPING global Ad Placements for pre-roll');
      adBreaksPlayedRef.current.add(1); // Mark as "played" so it doesn't retry
      return false;
    }
    
    // Fallback to global ad settings pre-roll
    if (adSettings && !adSettings.ad_break_1_enabled) {
      console.log('[Ads] Pre-roll (Ad Break 1) is disabled');
      return false;
    }

    adBreaksPlayedRef.current.add(1);
    console.log('[Ads] Playing Ad Break 1 (Pre-roll)...');

    try {
      // Ask server which ad to serve for Ad Break 1
      const res = await axios.get(`${API}/api/vod/admin/ads/serve?ad_break=1`);
      const { ad_type, ad, vast_tag } = res.data;

      console.log('[Ads] Server response:', ad_type);

      if (ad_type === 'custom' && ad) {
        // Play custom ad from R2
        playCustomAd(ad, 1);
        return true;
      } else {
        // Play Google ad
        const tagToUse = vast_tag || DEFAULT_VAST_TAG;
        
        if (!initializedRef.current) {
          const initialized = initializeIMA(tagToUse);
          if (!initialized) {
            console.log('[Ads] Failed to initialize IMA');
            return false;
          }
        }

        const videoElement = videoRef?.current;
        if (!adsLoaderRef.current || !videoElement) {
          return false;
        }

        // Initialize ad display container
        adDisplayContainerRef.current?.initialize();

        // Create ads request
        const correlator = Date.now();
        const tagWithCorrelator = tagToUse.includes('correlator=') 
          ? tagToUse.replace(/correlator=\d*/, `correlator=${correlator}`)
          : tagToUse + `&correlator=${correlator}`;

        const globalAdDims = getAdSlotDimensions();
        
        // Log dimensions for debugging
        console.log('[Ads] Ad request dimensions:', globalAdDims);
        
        // Ensure we have valid dimensions (minimum 100x56 for 16:9)
        if (globalAdDims.width < 100 || globalAdDims.height < 56) {
          console.warn('[Ads] Invalid ad dimensions, using fallback');
          globalAdDims.width = Math.max(globalAdDims.width, 640);
          globalAdDims.height = Math.max(globalAdDims.height, 360);
        }
        
        const adsRequest = new window.google.ima.AdsRequest();
        adsRequest.adTagUrl = tagWithCorrelator;
        adsRequest.linearAdSlotWidth = globalAdDims.width;
        adsRequest.linearAdSlotHeight = globalAdDims.height;
        adsRequest.nonLinearAdSlotWidth = globalAdDims.width;
        adsRequest.nonLinearAdSlotHeight = Math.floor(globalAdDims.height / 3);

        console.log('[Ads] Requesting Google ad with size:', globalAdDims.width, 'x', globalAdDims.height);
        adsLoaderRef.current.requestAds(adsRequest);
        
        return true;
      }
    } catch (error) {
      console.error('[Ads] Error fetching ad to serve:', error);
      return false;
    }
  }, [enableAds, adSettings, hasCustomAdSettings, cuePointsLoaded, videoCuePoints, playCustomAd, initializeIMA, videoRef]);

  // Generic ad play function for any ad break
  const playAdBreak = useCallback(async (breakNumber) => {
    if (!enableAds || adPlaying || adBreaksPlayedRef.current.has(breakNumber)) {
      console.log(`[Ads] Skipping Ad Break ${breakNumber}: enableAds=${enableAds}, adPlaying=${adPlaying}, alreadyPlayed=${adBreaksPlayedRef.current.has(breakNumber)}`);
      return false;
    }
    
    try {
      console.log(`[Ads] Playing Ad Break ${breakNumber}...`);
      
      // Pause the video
      videoRef?.current?.pause();
      
      // Ask server which ad to serve for this specific ad break
      const res = await axios.get(`${API}/api/vod/admin/ads/serve?ad_break=${breakNumber}`);
      const { ad_type, ad, vast_tag } = res.data;
      
      console.log(`[Ads] Server returned ad_type=${ad_type}, ad=${ad?.title || 'none'}`);

      if (ad_type === 'custom' && ad) {
        // Mark as played ONLY after we're about to play
        adBreaksPlayedRef.current.add(breakNumber);
        playCustomAd(ad, breakNumber);
        return true;
      } else if (ad_type === 'vast' || ad_type === 'google') {
        // Play Google/VAST ad
        const tagToUse = vast_tag || DEFAULT_VAST_TAG;
        
        if (!initializedRef.current) {
          const initialized = initializeIMA(tagToUse);
          if (!initialized) {
            console.log('[Ads] IMA initialization failed, resuming video');
            videoRef?.current?.play().catch(() => {});
            return false;
          }
        }

        const videoElement = videoRef?.current;
        if (!adsLoaderRef.current || !videoElement) {
          console.log('[Ads] No adsLoader or video element, resuming video');
          videoRef?.current?.play().catch(() => {});
          return false;
        }

        // Mark as played ONLY after we're about to request ads
        adBreaksPlayedRef.current.add(breakNumber);
        
        adDisplayContainerRef.current?.initialize();

        const correlator = Date.now();
        const tagWithCorrelator = tagToUse.includes('correlator=') 
          ? tagToUse.replace(/correlator=\d*/, `correlator=${correlator}`)
          : tagToUse + `&correlator=${correlator}`;

        const midRollDims = getAdSlotDimensions();
        const adsRequest = new window.google.ima.AdsRequest();
        adsRequest.adTagUrl = tagWithCorrelator;
        adsRequest.linearAdSlotWidth = midRollDims.width;
        adsRequest.linearAdSlotHeight = midRollDims.height;
        adsRequest.nonLinearAdSlotWidth = midRollDims.width;
        adsRequest.nonLinearAdSlotHeight = Math.floor(midRollDims.height / 3);

        adsLoaderRef.current.requestAds(adsRequest);
        return true;
      } else {
        console.log('[Ads] No ad to play for this break, resuming video');
        videoRef?.current?.play().catch(() => {});
        return false;
      }
    } catch (error) {
      console.error(`[Ads] Error playing Ad Break ${breakNumber}:`, error);
      videoRef?.current?.play().catch(() => {});
      return false;
    }
  }, [enableAds, adPlaying, playCustomAd, initializeIMA, videoRef, getAdSlotDimensions]);

  // Check for ad breaks based on video time
  const checkAdBreaks = useCallback((currentTime) => {
    // Skip global ad breaks if video has custom Ad Cue Points
    if (hasCustomAdSettings) {
      return;
    }
    
    if (!adSettings) {
      return;
    }
    
    if (adPlayingRef.current) {
      // Don't check while ad is already playing
      return;
    }
    
    // Check ad breaks 2-5 (mid-rolls at specific times)
    // Note: Times are in SECONDS (e.g., 360 = 6 minutes)
    const adBreaks = [
      { num: 2, enabled: adSettings.ad_break_2_enabled, time: adSettings.ad_break_2_time },
      { num: 3, enabled: adSettings.ad_break_3_enabled, time: adSettings.ad_break_3_time },
      { num: 4, enabled: adSettings.ad_break_4_enabled, time: adSettings.ad_break_4_time },
      { num: 5, enabled: adSettings.ad_break_5_enabled, time: adSettings.ad_break_5_time },
    ];
    
    for (const adBreak of adBreaks) {
      if (!adBreak.enabled) continue;
      if (adBreaksPlayedRef.current.has(adBreak.num)) continue;
      
      // IMPROVED: Trigger if we've passed the ad break time (not just within a window)
      // This ensures ads trigger even if timeupdate events are slow/infrequent
      if (currentTime >= adBreak.time) {
        console.log(`[Ads] >>> TRIGGERING Ad Break ${adBreak.num} at ${currentTime.toFixed(1)}s (target was: ${adBreak.time}s / ${(adBreak.time/60).toFixed(1)}min) <<<`);
        playAdBreak(adBreak.num);
        return; // Only trigger one ad at a time
      }
    }
  }, [adSettings, hasCustomAdSettings, playAdBreak]);
  
  // Play a video-specific cue point ad
  const playCuePointAd = useCallback(async (cuePoint, cueIndex) => {
    if (adPlaying) return false;
    
    const cueKey = `cue_${cueIndex}_${cuePoint.timestamp}`;
    if (cuePointsTriggeredRef.current.has(cueKey)) return false;
    
    cuePointsTriggeredRef.current.add(cueKey);
    console.log(`[Ads] Playing cue point ad at ${cuePoint.timestamp}s, type: ${cuePoint.ad_type}`);
    
    const videoElement = videoRef?.current;
    
    if (videoElement) {
      videoElement.pause();
    }
    
    try {
      if (cuePoint.ad_type === 'custom' && cuePoint.custom_url) {
        // Play custom ad video
        const customAdData = {
          id: `cue_${cueIndex}`,
          video_url: cuePoint.custom_url,
          duration: cuePoint.duration || 15,
          skip_after: 5
        };
        playCustomAd(customAdData, `cue_${cueIndex}`);
        return true;
      } else {
        // Play Google AdSense ad
        const tagToUse = adSettings?.vast_tag_url || DEFAULT_VAST_TAG;
        
        if (!initializedRef.current) {
          const initialized = initializeIMA(tagToUse);
          if (!initialized) {
            videoElement?.play().catch(() => {});
            return false;
          }
        }
        
        if (!adsLoaderRef.current || !videoElement) {
          videoElement?.play().catch(() => {});
          return false;
        }
        
        adDisplayContainerRef.current?.initialize();
        
        const correlator = Date.now();
        const tagWithCorrelator = tagToUse.includes('correlator=') 
          ? tagToUse.replace(/correlator=\d*/, `correlator=${correlator}`)
          : tagToUse + `&correlator=${correlator}`;
        
        const cuePointDims = getAdSlotDimensions();
        const adsRequest = new window.google.ima.AdsRequest();
        adsRequest.adTagUrl = tagWithCorrelator;
        adsRequest.linearAdSlotWidth = cuePointDims.width;
        adsRequest.linearAdSlotHeight = cuePointDims.height;
        
        adsLoaderRef.current.requestAds(adsRequest);
        return true;
      }
    } catch (error) {
      console.error('[Ads] Error playing cue point ad:', error);
      videoElement?.play().catch(() => {});
      return false;
    }
  }, [adPlaying, playCustomAd, initializeIMA, videoRef, adSettings, getAdSlotDimensions]);
  
  // Check for video-specific cue points
  const checkVideoCuePoints = useCallback((currentTime) => {
    if (!videoCuePoints || adPlaying) return;
    
    // Check mid-roll cue points
    const midRolls = videoCuePoints.mid_rolls || [];
    for (let i = 0; i < midRolls.length; i++) {
      const cue = midRolls[i];
      const cueKey = `cue_${i}_${cue.timestamp}`;
      
      if (!cuePointsTriggeredRef.current.has(cueKey)) {
        // Trigger if within 2 second window of cue point
        if (currentTime >= cue.timestamp && currentTime < cue.timestamp + 2) {
          console.log(`[Ads] >>> TRIGGERING Cue Point at ${currentTime.toFixed(1)}s (target: ${cue.timestamp}s) <<<`);
          playCuePointAd(cue, i);
          break; // Only trigger one ad at a time
        }
      }
    }
  }, [videoCuePoints, adPlaying, playCuePointAd]);

  // Set up timeupdate listener on video element for mid-roll triggers
  useEffect(() => {
    if (!enableAds) {
      console.log('[Ads] Mid-roll check: disabled');
      return;
    }
    
    // Need either adSettings or videoCuePoints
    if (!adSettings && !videoCuePoints) {
      console.log('[Ads] Mid-roll check: no settings or cue points');
      return;
    }

    const videoElement = videoRef?.current;
    if (!videoElement) {
      console.log('[Ads] Mid-roll check: no video element');
      return;
    }

    console.log('[Ads] Setting up timeupdate listener for mid-roll ads');
    if (adSettings) {
      console.log('[Ads] Ad break settings:', {
        break2: { enabled: adSettings.ad_break_2_enabled, time: adSettings.ad_break_2_time },
        break3: { enabled: adSettings.ad_break_3_enabled, time: adSettings.ad_break_3_time },
      });
    }
    if (videoCuePoints) {
      console.log('[Ads] Video cue points:', videoCuePoints);
    }
    
    let lastCheckedSecond = -1;
    
    const handleTimeUpdate = () => {
      const currentTime = videoElement.currentTime;
      const currentSecond = Math.floor(currentTime);
      
      // Only check once per second to avoid spam
      if (currentSecond !== lastCheckedSecond) {
        lastCheckedSecond = currentSecond;
        
        // Check global ad breaks from adSettings ONLY if video doesn't have custom Ad Cue Points
        if (adSettings && !hasCustomAdSettings) {
          const nearAdBreak = [
            adSettings.ad_break_2_time,
            adSettings.ad_break_3_time,
            adSettings.ad_break_4_time,
            adSettings.ad_break_5_time
          ].some(t => Math.abs(currentSecond - t) <= 5);
          
          if ((currentSecond % 30 === 0 && currentSecond > 0) || nearAdBreak) {
            console.log(`[Ads] Video at ${currentSecond}s, checking for mid-roll ads (global Ad Placements)...`);
          }
          
          checkAdBreaks(currentTime);
        }
        
        // Check video-specific cue points (Ad Cue Points editor)
        if (videoCuePoints && videoCuePoints.mid_rolls?.length > 0) {
          checkVideoCuePoints(currentTime);
        }
      }
    };
    
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    console.log('[Ads] timeupdate listener attached to video element');
    
    // MOBILE/FULLSCREEN FIX: Also set up interval-based fallback for iOS Safari fullscreen
    // iOS native fullscreen doesn't consistently fire timeupdate events
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    let fallbackInterval = null;
    
    if (isMobile) {
      console.log('[Ads] Mobile detected - adding fallback interval for fullscreen ad checks');
      fallbackInterval = setInterval(() => {
        // Only check if video is playing
        // Note: We check adPlayingRef instead of adPlaying to avoid stale closure issues
        if (videoElement && !videoElement.paused && !adPlayingRef.current) {
          const currentTime = videoElement.currentTime;
          const currentSecond = Math.floor(currentTime);
          
          // Log near ad break times for debugging
          const nearAdBreak = adSettings && [
            adSettings.ad_break_2_time,
            adSettings.ad_break_3_time,
            adSettings.ad_break_4_time,
            adSettings.ad_break_5_time
          ].some(t => t && Math.abs(currentTime - t) <= 10);
          
          if (nearAdBreak) {
            console.log(`[Ads] MOBILE FALLBACK: Near ad break at ${currentTime.toFixed(1)}s`);
          }
          
          // Check global ad breaks
          if (adSettings && !hasCustomAdSettings) {
            checkAdBreaks(currentTime);
          }
          
          // Check video-specific cue points
          if (videoCuePoints && videoCuePoints.mid_rolls?.length > 0) {
            checkVideoCuePoints(currentTime);
          }
        }
      }, 250); // Check every 250ms for more responsive ad triggering on mobile
    }
    
    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      if (fallbackInterval) {
        clearInterval(fallbackInterval);
        console.log('[Ads] Fallback interval cleared');
      }
      console.log('[Ads] timeupdate listener removed');
    };
  }, [enableAds, adSettings, videoCuePoints, hasCustomAdSettings, checkAdBreaks, checkVideoCuePoints, videoRef]);

  // Play post-roll ad (Ad Break 6 or video-specific post-roll)
  const playPostRoll = useCallback(async () => {
    if (!enableAds || adBreaksPlayedRef.current.has(6)) {
      return false;
    }
    
    // First check video-specific post-roll from cue point editor
    if (videoCuePoints?.post_roll) {
      adBreaksPlayedRef.current.add(6);
      console.log('[Ads] Playing video-specific post-roll...');
      
      const postRoll = videoCuePoints.post_roll;
      if (postRoll.ad_type === 'custom' && postRoll.custom_url) {
        const customAdData = {
          id: 'post_roll_cue',
          video_url: postRoll.custom_url,
          duration: 15,
          skip_after: 5
        };
        playCustomAd(customAdData, 'post_roll');
        return true;
      } else {
        // Google ad for post-roll
        const tagToUse = adSettings?.vast_tag_url || DEFAULT_VAST_TAG;
        if (!initializedRef.current) {
          const initialized = initializeIMA(tagToUse);
          if (!initialized) return false;
        }
        
        const videoElement = videoRef?.current;
        if (!adsLoaderRef.current || !videoElement) return false;
        
        adDisplayContainerRef.current?.initialize();
        const correlator = Date.now();
        const tagWithCorrelator = tagToUse + `&correlator=${correlator}`;
        
        const postRollDims = getAdSlotDimensions();
        const adsRequest = new window.google.ima.AdsRequest();
        adsRequest.adTagUrl = tagWithCorrelator;
        adsRequest.linearAdSlotWidth = postRollDims.width;
        adsRequest.linearAdSlotHeight = postRollDims.height;
        
        adsLoaderRef.current.requestAds(adsRequest);
        return true;
      }
    }
    
    // If video has custom Ad Cue Points settings, skip global Ad Placements entirely
    if (hasCustomAdSettings) {
      console.log('[Ads] Video has custom Ad Cue Points - SKIPPING global Ad Placements for post-roll');
      adBreaksPlayedRef.current.add(6); // Mark as "played" so it doesn't retry
      return false;
    }
    
    // Fallback to global ad settings post-roll
    if (!adSettings?.ad_break_6_enabled) {
      return false;
    }
    
    console.log('[Ads] Playing Ad Break 6 (Post-roll)...');
    return playAdBreak(6);
  }, [enableAds, adSettings, videoCuePoints, hasCustomAdSettings, cuePointsLoaded, playAdBreak, playCustomAd, initializeIMA, videoRef]);

  // Check if pre-roll is enabled
  const shouldPlayPreRoll = useCallback(() => {
    // Wait for cue points to load before deciding
    if (!cuePointsLoaded) {
      return false; // Don't play until we know if video has custom settings
    }
    // Video-specific pre-roll takes precedence
    if (videoCuePoints?.pre_roll) {
      return true;
    }
    // If video has custom settings but no pre-roll, don't play global pre-roll
    if (hasCustomAdSettings) {
      console.log('[Ads] shouldPlayPreRoll: Video has custom Ad Cue Points, skipping global pre-roll');
      return false;
    }
    return adSettings?.ad_break_1_enabled !== false; // Default to true
  }, [adSettings, videoCuePoints, hasCustomAdSettings, cuePointsLoaded]);

  // Reset ads state
  const resetAds = useCallback(() => {
    adBreaksPlayedRef.current = new Set();
    initializedRef.current = false;
    setAdPlaying(false);
    setAdType(null);
    setCustomAd(null);
    setShowSkipButton(false);
    
    if (skipTimerRef.current) {
      clearInterval(skipTimerRef.current);
    }
    
    if (adCheckIntervalRef.current) {
      clearInterval(adCheckIntervalRef.current);
    }
    
    if (customAdVideoRef.current) {
      customAdVideoRef.current.pause();
      customAdVideoRef.current.remove();
      customAdVideoRef.current = null;
    }
    
    if (adsManagerRef.current) {
      adsManagerRef.current.destroy();
      adsManagerRef.current = null;
    }
    if (adsLoaderRef.current) {
      adsLoaderRef.current.destroy();
      adsLoaderRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (skipTimerRef.current) {
        clearInterval(skipTimerRef.current);
      }
      if (adCheckIntervalRef.current) {
        clearInterval(adCheckIntervalRef.current);
      }
      if (adsManagerRef.current) {
        adsManagerRef.current.destroy();
      }
      if (adsLoaderRef.current) {
        adsLoaderRef.current.destroy();
      }
    };
  }, []);

  return {
    adPlaying,
    adPaused,
    adType,
    customAd,
    imaReady,
    showSkipButton,
    skipCountdown,
    adSettings,
    videoCuePoints, // Video-specific cue points from Ad Cue Point Editor
    playPreRoll,
    playPostRoll,
    playAdBreak,
    shouldPlayPreRoll,
    skipCustomAd,
    pauseAd,
    resumeAd,
    toggleAdPlayPause,
    resetAds,
    resizeImaAds, // Call this when fullscreen/orientation changes
    hasCustomAdSettings, // Expose this so VODVideoDetail can check it
    cuePointsLoaded // Expose this so caller knows when cue points are loaded
  };
};

export default useEnhancedVideoAds;
